/**
 * デジタルサイネージ 主題図（避難・避難所）画面用モジュール
 *
 * @module app/signage/display/SignageThematicEvacPage
 */
define(['module',
    'app/config',
    'app/map/legend/_LegendPaneBase',
    'app/map/legend/LEGEND_LIST',
    'dojo/_base/array',
    'dojo/_base/declare',
    'dojo/_base/lang',
    'dojo/promise/all',
    'dojo/text!./templates/SignageThematicEvacPage.html',
    'idis/map/IdisMap',
    'idis/service/Requester',
    '../_SignageBase',
], function (module, config, _LegendPaneBase, LEGEND_LIST, array, declare, lang, all, template, IdisMap, Requester, _SignageBase) {

    // レイヤ情報カテゴリコード
    var INFO_CATEGORY_CD = {
        EVAC_ORDER: 'D_EVAC_ORDER',
        SHELTER: 'D_SHELTER'
    };

    // 地図初期表示セット
    var MAP_VIEW_CONFIG = {
        // 緯度経度
        latLng: [config.map.latitude, config.map.longitude],
        // ズーム
        zoom: 9,
    };

    /**
     *
     * @class SignageThematicEvacPage
     * @extends module:idis/view/page/_PageBase~_PageBase
     */
    return declare(module.id.replace(/\//g, '.'), [_SignageBase, _LegendPaneBase],
        /** @lends module:app/signage/display/SignageThematicEvacPage~SignageThematicEvacPage# */
        {
            // テンプレート文字列
            templateString: template,

            // レイヤ追加用モジュール
            _layerControl: null,

            // ルート要素に付与されるCSS
            baseClass: 'idis-Page map-LegendPane idis-Page--signage',

            constructor: function () {
                this.inherited(arguments);
            },

            buildRendering: function () {
                this.inherited(arguments);
            },

            startup: function () {
                this.inherited(arguments);
                this.initPage();
            },

            destroy: function () {
                this.inherited(arguments);
            },

            /**
             * 画面を初期化する
             */
            initPage: function () {
                // マップを初期化
                this.initMap();

                // 行政界レイヤーを表示
                this.putFirstLayer();

                // 避難・避難所情報レイヤ情報を取得
                Requester.get('/data/signage/' + this._screenId + '.json').then(lang.hitch(this, function (screenData) {
                    // 避難情報レイヤーを表示
                    this.showEvacOrderLayer(screenData.evacOrderProperties);
                    // 避難所レイヤーを表示
                    this.showShelterLayer(screenData.shelterFeatureCollection);
                }));

                // 凡例を表示
                this.showLegend();
            },

            /**
             * 地図を初期化する
             */
            initMap: function () {
                this.map = new IdisMap(this.id + '-map', {
                    config: config.map,
                    keyboard: false, // コメント時に+/-が使用できないため
                    touchExtend: false, // IE対応
                    minZoom: 9,
                    maxZoom: 18,
                    drawControlTooltips: false,
                    zoomControl: false
                }).setView(MAP_VIEW_CONFIG.latLng, MAP_VIEW_CONFIG.zoom);

                // mapのdestroyはremoveでDojoと互換なのでownで消せる
                this.own(this.map);

                this._layerControl = this.map.layerControl;
            },

            /**
             * 避難情報レイヤーを表示する
             */
            showEvacOrderLayer: function (evacOrderProperties) {
                // 避難発令がなければ処理終了
                if (!evacOrderProperties || evacOrderProperties.length === 0) {
                    return;
                }

                // 避難発令対象の市町村一覧を取得
                var municipalityCdList = [];
                array.forEach(evacOrderProperties, function (item) {
                    if (array.indexOf(municipalityCdList, item.municipalityCd) === -1) {
                        municipalityCdList.push(item.municipalityCd);
                    }
                }, this);

                // 市町村ごとの地区GeoJSON一覧を取得
                all(municipalityCdList.map(function (municipalityCd) {
                    // ベースとなるgeojsonファイルはキャッシュを効かせる
                    return Requester.get(`/data/master/district/${municipalityCd}.geojson`, { preventCache: false });
                })).then(lang.hitch(this, function (evacOrderBaseGeoJsonList) {
                    // 避難情報レイヤ追加
                    this._layerControl.addThematicLayer(INFO_CATEGORY_CD.EVAC_ORDER,
                        evacOrderBaseGeoJsonList,
                        // 避難情報レイヤstyleで利用するUI定義等
                        {
                            hasWarningLevel: this.hasWarningLevel,
                            evacOrderConfig: this.evacOrderConfig,
                            issueReasonTypeLabel: this.issueReasonTypeLabel,
                        },
                        // 属性情報
                        evacOrderProperties);
                }), function (error) {
                    console.error(error);
                });
            },

            /**
             * 避難所レイヤーを表示する
             */
            showShelterLayer: function (shelterFeatureCollection) {
                // 避難所開設情報がなければ処理終了
                if (!shelterFeatureCollection || shelterFeatureCollection.length === 0) {
                    return;
                }

                // 避難所GeoJson作成
                var shelterGeojson = {
                    type: "FeatureCollection",
                    features: shelterFeatureCollection
                };

                // 避難所レイヤ追加
                this._layerControl.addThematicLayer(INFO_CATEGORY_CD.SHELTER, shelterGeojson);
            },

            /**
             * 凡例を表示する
             */
            showLegend: function () {
                // 凡例一覧から避難・避難所の凡例を取得する
                var legendListToShow = array.filter(LEGEND_LIST, function (legend) {
                    return legend.code && [
                        INFO_CATEGORY_CD.EVAC_ORDER, INFO_CATEGORY_CD.SHELTER
                    ].includes(legend.code);
                });

                // 避難情報を先頭にするようにソート
                legendListToShow.sort(function (a) {
                    return a.code === INFO_CATEGORY_CD.EVAC_ORDER ? -1 : 1;
                });

                // 結果を配置
                this._placeLegendList(legendListToShow);
            }
        });
});
